import React, {
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import "./GroupedRechargeOffer.scss";
import type {
  GroupedRechargeOfferProps,
  GroupedRechargeSubTask,
} from "./GroupedRechargeOffer.types";
import { OptimizedImage } from "../../../../Base";
import { Icon } from "../../../../Icon";
import { useNavigation, ROUTES } from "../../../../../utils/navigation";
import { useCurrencySymbol } from "../../../../../hooks/useUtils";
import { Trans } from "react-i18next";
import { useI18n } from "@/i18n/hooks";
import utils from "@/utils/utils";
import MoneyImg from "@/assets/event/groupedRechargeMoney.webp";
import StackableRibbon from "@/assets/event/groupedRechargeStackable.webp";
import CountdownIcon from "@/assets/event/groupedRechargeCountdownIcon.svg";
import WalletStud from "@/assets/event/groupedRechargeWalletStud.svg";
import { getMinPositiveGroupedRechargeCountdownMs } from "../../../groupedRechargeTaskFloatingId";
import { useHomeDisplayStore } from "@/modules/homeDisplay/stores";

/** 与接口剩余毫秒对齐：仅当服务端明显变短时用新值（刷新 / refreshConfig） */
const GROUPED_RECHARGE_COUNTDOWN_SYNC_MS = 2000;

/** 无持久化：每次挂载从快照开始（浮标条等） */
const GroupedRechargePillHmsCountdownEphemeral: React.FC<{
  remainingMs: number;
}> = memo(({ remainingMs }) => {
  const [currentMs, setCurrentMs] = useState(remainingMs);

  useEffect(() => {
    setCurrentMs(remainingMs);
  }, [remainingMs]);

  useEffect(() => {
    if (currentMs <= 0) return;
    const t = setInterval(
      () => setCurrentMs(prev => Math.max(0, prev - 1000)),
      1000
    );
    return () => clearInterval(t);
  }, [currentMs]);

  const hms = useMemo(() => {
    const s = Math.floor(Math.max(0, currentMs) / 1000);
    const hh = String(Math.floor(s / 3600)).padStart(2, "0");
    const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${hh} : ${mm} : ${ss}`;
  }, [currentMs]);

  if (currentMs <= 0) return null;

  return (
    <span className="grouped-recharge__countdown-strip-digits">{hms}</span>
  );
});
GroupedRechargePillHmsCountdownEphemeral.displayName =
  "GroupedRechargePillHmsCountdownEphemeral";

/**
 * 充值解锁弹窗：config 快照在关闭再开时不变，用 sessionStorage 记绝对截止时间，
 * 避免秒数「回满」；接口剩余明显变短时再对齐服务端。
 */
const GroupedRechargePillHmsCountdownPersisted: React.FC<{
  snapshotRemainingMs: number;
  storageKey: string;
}> = ({ snapshotRemainingMs, storageKey }) => {
  const deadlineRef = useRef<number>(NaN);
  const [displayMs, setDisplayMs] = useState(0);

  useLayoutEffect(() => {
    const now = Date.now();
    const srvRem = snapshotRemainingMs;
    const hasServerRemaining = Number.isFinite(srvRem) && srvRem > 0;

    if (!hasServerRemaining) {
      deadlineRef.current = NaN;
      setDisplayMs(0);
      try {
        if (typeof sessionStorage !== "undefined") {
          sessionStorage.removeItem(storageKey);
        }
      } catch {
        /* ignore */
      }
      return;
    }

    let storedDeadline = NaN;
    try {
      if (typeof sessionStorage !== "undefined") {
        const raw = sessionStorage.getItem(storageKey);
        if (raw != null) {
          const parsed = Number(raw);
          if (!Number.isFinite(parsed)) {
            sessionStorage.removeItem(storageKey);
          } else if (parsed <= now) {
            sessionStorage.removeItem(storageKey);
          } else {
            storedDeadline = parsed;
          }
        }
      }
    } catch {
      /* ignore */
    }

    const serverDeadline = now + srvRem;
    let deadline = storedDeadline;

    if (!Number.isFinite(deadline)) {
      deadline = serverDeadline;
    } else {
      const wallRem = deadline - now;
      if (srvRem + GROUPED_RECHARGE_COUNTDOWN_SYNC_MS < wallRem) {
        deadline = serverDeadline;
      }
    }

    deadlineRef.current = deadline;
    const rem = Math.max(0, Math.floor(deadline - Date.now()));
    setDisplayMs(rem);

    try {
      if (typeof sessionStorage !== "undefined" && Number.isFinite(deadline)) {
        sessionStorage.setItem(storageKey, String(Math.floor(deadline)));
      }
    } catch {
      /* ignore */
    }
  }, [snapshotRemainingMs, storageKey]);

  useEffect(() => {
    const bump = () => {
      const d = deadlineRef.current;
      const next =
        Number.isFinite(d) && d > Date.now()
          ? Math.max(0, Math.floor(d - Date.now()))
          : 0;
      setDisplayMs(next);
    };
    bump();
    const id = window.setInterval(bump, 1000);
    return () => window.clearInterval(id);
  }, [snapshotRemainingMs, storageKey]);

  const hms = useMemo(() => {
    const s = Math.floor(Math.max(0, displayMs) / 1000);
    const hh = String(Math.floor(s / 3600)).padStart(2, "0");
    const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${hh} : ${mm} : ${ss}`;
  }, [displayMs]);

  if (displayMs <= 0) return null;

  return (
    <span className="grouped-recharge__countdown-strip-digits">{hms}</span>
  );
};
GroupedRechargePillHmsCountdownPersisted.displayName =
  "GroupedRechargePillHmsCountdownPersisted";

// adjustedCountdownMs（由 lastConfigFetchedAt 校正）已在父组件中扣除了"API 拉取→现在"的
// 流逝时间，Ephemeral 每次挂载都从正确的剩余量开始计时，无需再用 sessionStorage 持久化。
// 移除 Persisted 分支可避免 config 刷新后 sessionStorage 旧值覆盖新值导致的不同步。
const GroupedRechargePillHmsCountdown: React.FC<{
  snapshotRemainingMs: number;
}> = memo(({ snapshotRemainingMs }) => {
  return (
    <GroupedRechargePillHmsCountdownEphemeral
      remainingMs={snapshotRemainingMs}
    />
  );
});
GroupedRechargePillHmsCountdown.displayName = "GroupedRechargePillHmsCountdown";

/** Trans 用命名标签，避免部分语言下 <0>/<1> 与插值解析异常导致连着逗号的整段被包进同一高亮节点 */
const GROUPED_RECHARGE_SUMMARY_TRANS_COMPONENTS = {
  amt1: <span className="grouped-recharge__summary-amount" />,
  amt2: <span className="grouped-recharge__summary-highlight" />,
};

// ---- Pending view（待充值）：任务列表 + 汇总（按钮已移至卡片外部） ----
const PendingView: React.FC<{
  currSymbol: string;
  totalRewardAmount: number;
  summaryOneTimeRechargeAmount: number;
  tasks: GroupedRechargeSubTask[];
  t: (key: string, params?: any) => string;
}> = ({
  currSymbol,
  totalRewardAmount,
  summaryOneTimeRechargeAmount,
  tasks,
  t,
}) => {
  const hasTasks = tasks.length > 0;
  const isMulti = tasks.length > 1;
  const r1 = `${currSymbol}${summaryOneTimeRechargeAmount}`;
  const r2 = `${currSymbol}${totalRewardAmount}`;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<{ top: number; height: number } | null>(
    null
  );

  const updateThumb = () => {
    const el = scrollRef.current;
    if (!el) {
      setThumb(null);
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight + 1) {
      setThumb(null);
      return;
    }
    const ratio = clientHeight / scrollHeight;
    const thumbH = Math.max(clientHeight * ratio, 16);
    const maxTop = clientHeight - thumbH;
    setThumb({
      top: (scrollTop / (scrollHeight - clientHeight)) * maxTop,
      height: thumbH,
    });
  };

  useEffect(() => {
    updateThumb();
  }, [tasks]);

  return (
    <>
      {/* 任务列表：虚线边框容器 */}
      {hasTasks && (
        <div className="grouped-recharge__tasks-frame">
          <div
            ref={isMulti ? scrollRef : undefined}
            className={[
              "grouped-recharge__tasks",
              isMulti ? "grouped-recharge__tasks--multi" : "",
            ]
              .join(" ")
              .trim()}
            onScroll={isMulti ? updateThumb : undefined}
          >
            {tasks.map((task, idx) => (
              <div
                key={task.taskId ?? idx}
                className="grouped-recharge__task-row"
              >
                <div className="grouped-recharge__task-icon">
                  {task.iconUrl ? (
                    <OptimizedImage
                      src={utils.getImageUrl(task.iconUrl) ?? task.iconUrl}
                      alt="task icon"
                      width="3rem"
                      height="3rem"
                    />
                  ) : null}
                </div>
                {/* <div className="grouped-recharge__task-divider" /> */}
                <div className="grouped-recharge__task-text">
                  <span className="grouped-recharge__task-label">
                    {t("event.groupedRecharge.recharge", {
                      defaultValue: "Recharge",
                    })}
                  </span>
                  <span className="grouped-recharge__task-amount">
                    {currSymbol}
                    {task.rechargeAmount}
                  </span>
                  <span className="grouped-recharge__task-label">
                    {t("event.groupedRecharge.unlock", {
                      defaultValue: "unlock",
                    })}
                  </span>
                  <span className="grouped-recharge__task-amount">
                    {currSymbol}
                    {task.rewardAmount}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {/* 自定义滚动条（无箭头） */}
          {isMulti && thumb && (
            <div className="grouped-recharge__scrollbar-track">
              <div
                className="grouped-recharge__scrollbar-thumb"
                style={{ top: thumb.top, height: thumb.height }}
              />
            </div>
          )}
        </div>
      )}

      {/* 底部汇总：Recharge $xxx to get all $xxx */}
      {tasks.length > 1 && (
        <div className="grouped-recharge__summary">
          <Trans
            i18nKey="event.groupedRecharge.summaryRechargeToGet"
            values={{ r1, r2 }}
            defaults="Recharge <amt1>{{r1}}</amt1> to get <amt2>all {{r2}}</amt2>"
            components={GROUPED_RECHARGE_SUMMARY_TRANS_COMPONENTS}
          />
        </div>
      )}
    </>
  );
};

// ---- Claimable view（充值成功可领取） ----
// ---- 主组件 ----
export const GroupedRechargeOffer: React.FC<GroupedRechargeOfferProps> = ({
  visible,
  onClose,
  taskData,
}) => {
  const { t } = useI18n();
  const { navigate } = useNavigation();
  const currSymbol = useCurrencySymbol();

  // 控制 Portal 挂载（visible=false 后保持渲染以播放关闭动画）
  const [mounted, setMounted] = useState(visible);
  // 控制进入/离开动画类：true 表示已进入（带动画到位）
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (visible) {
      setMounted(true);
      // 下一帧再切到 entered，确保从初始态过渡
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setEntered(true));
      });
      return () => cancelAnimationFrame(raf);
    }
    if (!mounted) return;
    setEntered(false);
    // 与 CSS 动画时长保持一致，结束后卸载
    const t = window.setTimeout(() => setMounted(false), 260);
    return () => window.clearTimeout(t);
  }, [visible, mounted]);

  // 解析子任务列表
  // 已确认的接口字段：minSingleRechargeAmount / rewardAmount / bonusIconUrl
  const subTasks = useMemo((): GroupedRechargeSubTask[] => {
    // 按优先级尝试各种可能的子任务数组字段名
    let raw: any[] =
      taskData?.tasks ??
      taskData?.taskList ??
      taskData?.taskItems ??
      taskData?.items ??
      taskData?.list ??
      taskData?.subTasks ??
      taskData?.rechargeTasks ??
      taskData?.claimableTiers ??
      taskData?.tiers ??
      taskData?.rechargeGroups ??
      taskData?.taskDetails ??
      [];

    // 兜底1：顶层有 minSingleRechargeAmount（已确认的 config 字段）
    if (raw.length === 0) {
      const mra =
        taskData?.minSingleRechargeAmount ??
        taskData?.rechargeAmount ??
        taskData?.requiredAmount;
      if (mra != null) {
        raw = [
          {
            taskId: taskData?.taskGroupId ?? taskData?.taskId ?? "single",
            rechargeAmount: Number(mra),
            rewardAmount: Number(taskData?.rewardAmount ?? 0),
            bonusIconUrl: taskData?.bonusIconUrl,
            iconUrl: taskData?.bonusIconUrl ?? taskData?.iconUrl,
            status: taskData?.status,
            paid: taskData?.paid,
            satisfied: taskData?.satisfied,
            remainingRechargeAmount: Number(
              (taskData as any)?.remainingRechargeAmount ?? 0
            ),
          },
        ];
      }
    }

    // 兜底2：遍历 taskData 所有属性，找第一个含有对象的非空数组
    if (raw.length === 0 && taskData != null) {
      const EXCLUDE_KEYS = new Set([
        "taskId",
        "taskGroupId",
        "id",
        "status",
        "claimRecords",
        "userProgress",
        "popupItems",
        "bonusPopupPending",
        "popupRewardAmount",
      ]);
      for (const key of Object.keys(taskData)) {
        if (EXCLUDE_KEYS.has(key)) continue;
        const val = (taskData as Record<string, unknown>)[key];
        if (
          Array.isArray(val) &&
          val.length > 0 &&
          typeof val[0] === "object" &&
          val[0] !== null
        ) {
          raw = val;
          break;
        }
      }
    }

    const mapped = raw.map((tk: any, i: number) => ({
      taskId: tk.taskId ?? tk.id ?? String(i),
      // minSingleRechargeAmount 为已确认字段，优先取
      rechargeAmount: Number(
        tk.minSingleRechargeAmount ??
          tk.rechargeAmount ??
          tk.requiredAmount ??
          tk.target ??
          tk.amount ??
          0
      ),
      rewardAmount: Number(
        tk.rewardAmount ??
          tk.rewardItemQuantity ??
          tk.reward ??
          tk.bonusAmount ??
          0
      ),
      // bonusIconUrl 为已确认字段，优先取
      iconUrl: tk.bonusIconUrl ?? tk.iconUrl ?? tk.icon ?? undefined,
      status: tk.status,
      paid: tk.paid,
      satisfied: tk.satisfied,
      remainingRechargeAmount: Number(tk.remainingRechargeAmount ?? 0),
    }));

    // 多活动时按单笔充值门槛从小到大展示
    return [...mapped].sort((a, b) => {
      if (a.rechargeAmount !== b.rechargeAmount) {
        return a.rechargeAmount - b.rechargeAmount;
      }
      return String(a.taskId ?? "").localeCompare(String(b.taskId ?? ""));
    });
  }, [taskData]);

  // 奖励总额：顶部大字、ClaimableView、底部汇总
  const totalRewardAmount = useMemo(
    () =>
      subTasks.length > 0
        ? subTasks.reduce((sum, tk) => sum + tk.rewardAmount, 0)
        : Number(taskData?.rewardAmount ?? 0),
    [subTasks, taskData]
  );

  /** 底部「一次性充值」说明用的门槛：接口优先，否则取各档最高 */
  const summaryOneTimeRechargeAmount = useMemo(() => {
    const td = taskData as {
      oneTimeRechargeTarget?: number;
      oneTimeRechargeAmount?: number;
    };
    const fromApi = td?.oneTimeRechargeTarget ?? td?.oneTimeRechargeAmount;
    if (fromApi != null) {
      const n = Number(fromApi);
      if (Number.isFinite(n) && n > 0) return n;
    }
    if (subTasks.length === 0) return 0;
    return Math.max(...subTasks.map(tk => tk.rechargeAmount));
  }, [subTasks, taskData]);

  // 顶部大字始终显示奖励总额
  const totalAmount = totalRewardAmount;

  // 从 store 实时读取最新的 countdownTime 和配置拉取时间戳
  // 浮标的 CountdownTimer 从 lastConfigFetchedAt 时刻开始计时，弹窗需扣除已流逝时间才能与浮标对齐
  const liveGrtList = useHomeDisplayStore(
    s =>
      (s.data?.activities as any)?.GROUPED_RECHARGE_TASK?.list as
        | readonly { countdownTime?: number | null }[]
        | null
        | undefined
  );
  const lastConfigFetchedAt = useHomeDisplayStore(s => s.lastConfigFetchedAt);

  const mergedListCountdownMs = useMemo(() => {
    const rawMs = getMinPositiveGroupedRechargeCountdownMs(
      liveGrtList && liveGrtList.length > 0 ? liveGrtList : taskData?.tasks
    );
    if (rawMs <= 0) return 0;
    // 扣除从配置拉取到现在已流逝的时间，与浮标倒计时对齐
    const elapsedMs =
      lastConfigFetchedAt > 0 ? Date.now() - lastConfigFetchedAt : 0;
    return Math.max(0, rawMs - elapsedMs);
  }, [liveGrtList, taskData, lastConfigFetchedAt]);

  /** 弹窗内倒计时 session 键：`userId` + 任务 id，缺失则沿用快照行为 */
  /**
   * 任务卡「下一轮充多少」：list 档位 remaining/minSingle（subTasks）；到账/Bonus Ready：popupItems+bonusPopupPending（勿用 list.paid）。
   * 数据源约定：groupedRechargeBonusFields.ts。
   */
  const hasRenderableTaskCard =
    subTasks.length > 0 ||
    Number(taskData?.rewardAmount ?? 0) > 0 ||
    totalRewardAmount > 0;

  if (!mounted) return null;
  if (!hasRenderableTaskCard) return null;

  const handleDeposit = () => {
    onClose();
    sessionStorage.setItem("autoSelectBestOffer", "true");
    navigate(ROUTES.RECHARGE);
  };

  const container = document.getElementById("root") ?? document.body;

  return createPortal(
    <div
      className={[
        "grouped-recharge",
        entered ? "grouped-recharge--entered" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* 暗色背景遮罩 */}
      <div className="grouped-recharge__mask" onClick={onClose} />

      {/* 内容区（礼物图 + 卡片 + 关闭按钮） */}
      <div className="grouped-recharge__container">
        {/* 卡片上方金币钱堆图片 */}
        <div className="grouped-recharge__hero">
          <img
            src={MoneyImg}
            alt="money"
            className="grouped-recharge__hero-img"
          />
        </div>

        <>
          {/* 白色信息卡（独立）：金额 + 副标题 */}
          <div className="grouped-recharge__info-card">
            <div className="grouped-recharge__card-ribbon" aria-hidden>
              <img src={StackableRibbon} alt="" />
              <span className="grouped-recharge__card-ribbon-text">
                {t("event.groupedRecharge.stackableRibbonText", {
                  defaultValue: "Stackable",
                })}
              </span>
            </div>
            <div className="grouped-recharge__total">
              <span className="grouped-recharge__total-symbol">
                {currSymbol}
              </span>
              {totalAmount.toFixed(2)}
            </div>
            <div className="grouped-recharge__subtitle">
              {t("event.groupedRecharge.bonusTitle", {
                defaultValue: "added to your wallet",
              })}
            </div>
            <div className="grouped-recharge__subtitle-hint">
              {t("event.groupedRecharge.stackableHint", {
                defaultValue: "Stackable with other offers",
              })}
            </div>
          </div>

          {/* 中间背景块 */}
          <div className="grouped-recharge__cards-bg" />

          {/* 橙色任务卡（独立）：倒计时 + 任务列表，搭扣挂在卡左侧 */}
          <div className="grouped-recharge__card">
            <img
              src={WalletStud}
              alt=""
              className="grouped-recharge__wallet-stud"
              aria-hidden
            />
            {mergedListCountdownMs > 0 && (
              <div
                className="grouped-recharge__countdown-strip"
                aria-label="countdown"
              >
                <img
                  src={CountdownIcon}
                  alt=""
                  className="grouped-recharge__countdown-icon"
                  aria-hidden
                />
                <div className="grouped-recharge__countdown-bg">
                  <GroupedRechargePillHmsCountdown
                    snapshotRemainingMs={mergedListCountdownMs}
                  />
                </div>
              </div>
            )}
            <PendingView
              currSymbol={currSymbol}
              totalRewardAmount={totalRewardAmount}
              summaryOneTimeRechargeAmount={summaryOneTimeRechargeAmount}
              tasks={subTasks}
              t={t}
            />
          </div>
        </>

        {/* 按钮（独立，在两张卡外） */}
        <div className="grouped-recharge__btn-wrap">
          <button className="grouped-recharge__btn" onClick={handleDeposit}>
            {t("common.deposit", { defaultValue: "Deposit" })}
          </button>
        </div>

        {/* 关闭按钮（与其他弹窗保持一致） */}
        <div
          className="grouped-recharge__close"
          onClick={onClose}
          role="button"
          aria-label="close"
        >
          <Icon name="closeDialog" color="#ffffff" />
        </div>
      </div>
    </div>,
    container
  );
};

export default GroupedRechargeOffer;
